import { useNavigate, useParams } from "react-router-dom";
import { useState, useEffect } from "react";
import { theme } from "../theme.js";
import LoadingOverlay from "../components/LoadingOverlay.jsx";

import Section from "../components/base/Section.jsx";
import Container from "../components/base/Container.jsx";
import Button from "../components/base/Button.jsx";
import EventCard from "../components/eventcard.jsx";

import Analytics from "../components/Chart/analytics.jsx";
import { PlusIcon } from "@heroicons/react/24/outline";

export default function OrganizationAnalytics() {
  const navigate = useNavigate();
  const { org_id } = useParams();
  
  const API_URL = process.env.REACT_APP_API_URL;

  const [user, setUser] = useState(null);
  const [organizer, setOrganizer] = useState(null);
  const [races, setRaces] = useState([]);
  const [events, setEvents] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [activeTab, setActiveTab] = useState("events");
  const [eventFilter, setEventFilter] = useState("all");
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Check authentication and authorization
  useEffect(() => {
    const userData = localStorage.getItem("user");
    const parsedUser = userData ? JSON.parse(userData) : null;
    setUser(parsedUser);

    if (!parsedUser) {
      setError("You must be logged in to access this dashboard");
      setLoading(false);
      return;
    }
  }, []);

  // Fetch organization details and verify ownership
  useEffect(() => {
    const fetchOrgDetails = async () => {
      try {
        const res = await fetch(`${API_URL}/api/organizers/${org_id}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setOrganizer(data);

        // Check if current user is the organizer (or authorized)
        // For now, we'll allow access if logged in
        setIsAuthorized(true);
      } catch (err) {
        console.error("Error fetching organizer:", err);
        setError(err.message || "Error fetching organizer");
      }
    };

    if (org_id && user) {
      fetchOrgDetails();
    }
  }, [org_id, user]);

  // Fetch races data
  useEffect(() => {
    const fetchRaces = async () => {
      try {
        const res = await fetch(`${API_URL}/api/races?organizer_id=${org_id}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setRaces(data || []);
      } catch (err) {
        console.error("Error fetching races:", err);
      }
    };

    if (org_id) {
      fetchRaces();
    }
  }, [org_id]);

  // Fetch events data
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const res = await fetch(`${API_URL}/api/events?organizer_id=${org_id}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setEvents(data || []);
        setEventsLoading(false);
      } catch (err) {
        console.error("Error fetching events:", err);
        setEventsLoading(false);
      }
    };

    if (org_id) {
      fetchEvents();
    }
  }, [org_id]);

  // Fetch registrations/participants for all races
  useEffect(() => {
    const fetchRegistrations = async () => {
      try {
        const allRegistrations = [];
        for (const race of races) {
          const res = await fetch(`${API_URL}/api/registrations?race_id=${race.id_bdd || race.id}`);
          if (res.ok) {
            const data = await res.json();
            allRegistrations.push(...(data || []));
          }
        }
        setLoading(false);
        setAnalyticsLoading(false);
      } catch (err) {
        console.error("Error fetching registrations:", err);
        setLoading(false);
        setAnalyticsLoading(false);
      }
    };

    if (races.length > 0) {
      fetchRegistrations();
    } else {
      setLoading(false);
      setAnalyticsLoading(false);
    }
  }, [races]);

  // Authorization denied
  if (!user) {
    return (
        <Container>
        <div style={{height: "80px", background: theme.colors.bgDark}}></div>
            <div style={styles.centerContent}>
            <h2>Access Denied</h2>
            <p>You must be logged in to access this dashboard</p>
            <Button title="Go to Login" onClick={() => navigate("/login")} />
            </div>
        </Container>
    );
  }

  if (error) {
    return (
        <Container>
        <div style={{height: "80px", background: theme.colors.bgDark}}></div>
            <div style={styles.centerContent}>
            <h2>⚠️ {error}</h2>
            <Button title="Back to Home" onClick={() => navigate("/")} />
            </div>
        </Container>
    );
  }

  if (loading) {
    return (      
      <LoadingOverlay isVisible={loading} message="Chargement du dashboard..." />
    );
  }

  // Filter events by date
  const now = new Date();
  const filteredEvents = events.filter((event) => {
    const endDate = event.end_date ? new Date(event.end_date) : null;
    if (eventFilter === "next") {
      return endDate && endDate > now;
    } else if (eventFilter === "previous") {
      return endDate && endDate <= now;
    }
    return true; // "all"
  });

  return (
    <>
      <div 
        style={{
          ...styles.pageLayout, 
          flexDirection: isMobile ? "column" : "row"
        }} 
        className="org-page-layout"
      >
        {/* Left Sidebar Menu */}
        <aside 
          style={{
            ...styles.sidebar,
            width: isMobile ? "100%" : "250px",
            minHeight: isMobile ? "auto" : "calc(100vh - 130px)",
            position: isMobile ? "relative" : "sticky",
            top: isMobile ? 0 : "130px",
            borderRight: isMobile ? "none" : `1px solid ${theme.colors.border}`,
            borderBottom: isMobile ? `1px solid ${theme.colors.border}` : "none",
          }} 
          className="org-sidebar"
        >
          <nav 
            style={{
              ...styles.nav,
              flexDirection: isMobile ? "row" : "column",
            }} 
            className="org-nav"
          >
            <button
              style={{
                ...styles.navItem,
                ...(activeTab === "events" ? styles.navItemActive : {})
              }}
              onClick={() => setActiveTab("events")}
            >
              Evènements
            </button>
            <button
              style={{
                ...styles.navItem,
                ...(activeTab === "analytics" ? styles.navItemActive : {})
              }}
              onClick={() => setActiveTab("analytics")}
            >
              Analyses
            </button>
          </nav>
        </aside>

        {/* Main Content */}
        <main 
          style={{
            ...styles.mainContent,
            padding: isMobile ? theme.spacing.md : theme.spacing.lg,
          }} 
          className="org-main-content"
        >
          {activeTab === "events" && (
            <Container style={styles.container}>
              <div style={styles.header}>
                <h1 style={styles.title}>{organizer?.name || "Organization"}</h1>
              </div>

              <Section 
                title={`Evènements (${filteredEvents.length})`} 
                border={false}
                headerAction={
                  <Button 
                    icon={PlusIcon}
                    title="Ajouter un évènement" 
                    onClick={() => navigate("/create-event")}
                    width="auto"
                  />
                }
              >
                <div style={styles.analyticsContent}>
                <div style={styles.filterAndButtonContainer}>
                  {/* Filter Buttons */}
                  <div style={styles.filterButtons}>
                    <button
                      style={{
                        ...styles.filterButton,
                        ...(eventFilter === "all" ? styles.filterButtonActive : {})
                      }}
                      onClick={() => setEventFilter("all")}
                    >
                      Tous
                    </button>
                    <button
                      style={{
                        ...styles.filterButton,
                        ...(eventFilter === "previous" ? styles.filterButtonActive : {})
                      }}
                      onClick={() => setEventFilter("previous")}
                    >
                      Passés
                    </button>
                    <button
                      style={{
                        ...styles.filterButton,
                        ...(eventFilter === "next" ? styles.filterButtonActive : {})
                      }}
                      onClick={() => setEventFilter("next")}
                    >
                      À venir
                    </button>
                  </div>
                </div>

                <div style={styles.list}>
                  {filteredEvents.length === 0 ? (
                    <p style={styles.emptyText}>Aucun évènement trouvé</p>
                  ) : (
                    <>
                      {filteredEvents.map((event, idx) => (
                        <EventCard event={event} key={idx} orgId={org_id} />
                      ))}
                      <LoadingOverlay isVisible={eventsLoading} message="Chargement des événements..." />
                   </>
                  )}
                </div>
                </div>
              </Section>
              <LoadingOverlay isVisible={eventsLoading} message="Chargement des événements..." />
            </Container>
          )}

          {activeTab === "analytics" && (
            isMobile ? (
              <Container style={styles.container}>
                <div style={styles.centerContent}>
                  <h2>Cette page n'est pas prise en charge sur cet appareil</h2>
                <Analytics />
              <p style={{ color: theme.colors.textMuted }}>Veuillez utiliser un écran plus grand pour consulter les analyses</p>
                </div>
              </Container>
            ) : (
              <Analytics />
            )
          )}
        </main>
      </div>
    </>
  );
}

function MetricCard({ value, label, icon }) {
  return (
    <div style={styles.metricCard}>
      <div style={styles.metricIcon}>{icon}</div>
      <div style={styles.metricValue}>{value}</div>
      <div style={styles.metricLabel}>{label}</div>
    </div>
  );
}

const styles = {
  pageLayout: {
    display: "flex",
    minHeight: "calc(100vh - 130px)",
    backgroundColor: theme.colors.bgLight,
  },
  sidebar: {
    width: "250px",
    minHeight: "calc(100vh - 130px)",
    backgroundColor: theme.colors.white,
    borderRight: `1px solid ${theme.colors.border}`,
    padding: theme.spacing.lg,
    boxShadow: theme.shadows.sm,
    position: "sticky",    
    zIndex: theme.zIndex.sticky,
    top: "130px",
    left: 0,
    right: 0,
    overflowY: "auto",
    flexShrink: 0,
  },
  nav: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing.sm,
  },
  navItem: {
    backgroundColor: "transparent",
    border: "none",
    padding: `${theme.spacing.md} ${theme.spacing.lg}`,
    fontSize: theme.fonts.size.sm,
    fontWeight: theme.fonts.weight.medium,
    cursor: "pointer",
    color: theme.colors.textMuted,
    borderRadius: theme.radius.sm,
    textAlign: "left",
    transition: "all 0.2s",
    "&:hover": {
      backgroundColor: theme.colors.bgLight,
    },
  },
  navItemActive: {
    backgroundColor: theme.colors.light,
    color: theme.colors.textInverted,
    fontWeight: theme.fonts.weight.bold,
  },
  mainContent: {
    flex: 1,
    backgroundColor: theme.colors.white,
    padding: theme.spacing.lg,
  },
  container: {
    padding: `${theme.spacing.xxxl} ${theme.spacing.xxl}`,
    backgroundColor: theme.colors.bgLight,
  },
  header: {
    marginTop: theme.spacing.huge,
    marginBottom: theme.spacing.xxxl,
    textAlign: "center",
    backgroundColor: theme.colors.white,
  },
  title: {
    fontSize: "32px",
    fontWeight: theme.fonts.weight.bold,
    color: theme.colors.textInverted,
    margin: 0,
  },
  subtitle: {
    fontSize: theme.fonts.size.lg,
    color: theme.colors.textMuted,
    margin: `${theme.spacing.sm} 0 0 0`,
  },
  filterAndButtonContainer: {
    display: "flex",
    gap: theme.spacing.lg,
    marginBottom: theme.spacing.lg,
    justifyContent: "space-between",
    alignItems: "center",
  },
  filterButtons: {
    display: "flex",
    gap: theme.spacing.sm,
    justifyContent: "flex-start",
  },
  filterButton: {
    padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
    border: `1px solid ${theme.colors.border}`,
    backgroundColor: theme.colors.white,
    color: theme.colors.textInverted,
    borderRadius: theme.radius.sm,
    cursor: "pointer",
    fontSize: theme.fonts.size.sm,
    fontWeight: theme.fonts.weight.medium,
    transition: "all 0.2s",
  },
  filterButtonActive: {
    backgroundColor: theme.colors.light,
    color: theme.colors.textInverted,
    border: `1px solid ${theme.colors.light}`,
  },
  metricsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: theme.spacing.lg,
    marginBottom: theme.spacing.xxxl,    
    marginLeft: theme.spacing.lg,
  },
  metricCard: {
    backgroundColor: theme.colors.white,
    padding: theme.spacing.lg,
    borderRadius: theme.radius.md,
    border: `1px solid ${theme.colors.border}`,
    textAlign: "center",
    boxShadow: theme.shadows.sm,
  },
  metricIcon: {
    fontSize: "32px",
    marginBottom: theme.spacing.sm,
  },
  metricValue: {
    fontSize: "28px",
    fontWeight: theme.fonts.weight.bold,
    color: theme.colors.light,
    margin: `${theme.spacing.xs} 0`,
  },
  metricLabel: {
    fontSize: theme.fonts.size.sm,
    color: theme.colors.textMuted,
    fontWeight: theme.fonts.weight.semibold,
  },
  analyticsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
    gap: theme.spacing.lg,
    marginBottom: theme.spacing.xxxl,
  },
  analyticsContent: {
    padding: theme.spacing.md,
    width: "100%",
  },
  list: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
    gap: theme.spacing.xxl,
    marginBottom: theme.spacing.xxl,
  },
  listItem: {
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    padding: theme.spacing.md,
    backgroundColor: theme.colors.bgLight,
    borderRadius: theme.radius.sm,
    border: `1px solid ${theme.colors.border}`,
  },
  eventName: {
    fontWeight: theme.fonts.weight.semibold,
    color: theme.colors.text,
  },
  eventRaces: {
    fontSize: theme.fonts.size.sm,
    color: theme.colors.textMuted,
  },
  raceName: {
    fontWeight: theme.fonts.weight.semibold,
    color: theme.colors.text,
  },
  raceStats: {
    fontSize: theme.fonts.size.sm,
    color: theme.colors.light,
    fontWeight: theme.fonts.weight.semibold,
  },
  emptyText: {
    textAlign: "center",
    color: theme.colors.textMuted,
    padding: theme.spacing.lg,
    margin: 0,
  },
  analyticsButtonContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "300px",
  },
  tableRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: `${theme.spacing.md} ${theme.spacing.lg}`,
    borderBottom: `1px solid ${theme.colors.border}`,
  },
  tableLabel: {
    color: theme.colors.textMuted,
    fontWeight: theme.fonts.weight.semibold,
  },
  tableValue: {
    fontSize: theme.fonts.size.lg,
    fontWeight: theme.fonts.weight.bold,
    color: theme.colors.light,
  },
  centerContent: {
    textAlign: "center",
    padding: theme.spacing.xxxl,
  },
  centerText: {
    textAlign: "center",
    color: theme.colors.textMuted,
    padding: theme.spacing.xxxl,
  },
};
